/**
 * /dashboard/hiring/requisitions/$id/pipeline — Pipeline Kanban board
 * (Phase H.8).
 *
 * GET /hiring/requisitions/{id}/pipeline is HR-admin OR the owning
 * hiring manager (server-resolved; a non-owner non-HR caller gets a
 * neutral 404). The response is the FULL board.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/hiring/requisitions/[id]/
 * pipeline/page.tsx. Swaps:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link Link → @tanstack/react-router Link
 */

import { useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, BarChart3, Pencil, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { matchesCandidateQuery } from "@/lib/hiring-recruiter";
import {
  useRequisitionPipeline,
  useRequisitions,
  hiringKeys,
} from "@/hooks/use-hiring";
import { hiringApi, toApiError } from "@/lib/api";
import { HiringModuleShell } from "@/components/shared/hiring-module-shell";
import {
  PHONE_BACK_LINK_CLASS,
  PhoneBackRow,
} from "@/components/hiring/phone-back-row";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { HiringFeatureGate } from "@/components/hiring/hiring-feature-gate";
import { ApplicationCard } from "@/components/hiring/application-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/hiring/requisitions/$id/pipeline",
)({
  // A9 hover-prefetch. Primary = the full board read, keyed by the path
  // param exactly like useRequisitionPipeline. The candidates name-map
  // query is deliberately NOT prefetched: it runs with staleTime 0 by
  // design (fresh names on every mount) and prefetching it would just
  // double the fetch. Malformed ids skip; errors fall through to the
  // component's own error/neutral-404 handling.
  loader: ({ context, params }) => {
    const id = Number(params.id);
    if (!Number.isFinite(id) || id <= 0) return;
    return context.queryClient
      .ensureQueryData({
        queryKey: hiringKeys.pipeline(id),
        queryFn: ({ signal }) => hiringApi.getRequisitionPipeline(id, signal),
      })
      .catch(() => undefined);
  },
  component: PipelineRoute,
});

function PipelineBoard({ id }: { readonly id: number }) {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const pipe = useRequisitionPipeline(id);
  // The job's title names the page ("Pipeline — Backend Engineer") when
  // the viewer's requisition list has it.
  const reqs = useRequisitions();
  const reqTitle = reqs.data?.requisitions.find((r) => r.id === id)?.title;
  // Card names come with the board itself (wave-B area 2): the hiring
  // manager can't list every candidate, so the old candidates-list lookup
  // left their cards as "Candidate #<id>". Hooks stay above the early
  // returns (the knockout toggle used to sit below them).
  const [hideKnockout, setHideKnockout] = useState(false);
  const [query, setQuery] = useState("");
  // Phones (< md) show one stage at a time; null = the first stage with
  // candidates in it.
  const [phoneStageId, setPhoneStageId] = useState<number | null>(null);

  if (pipe.isLoading) {
    return (
      <HiringModuleShell title="Pipeline">
        <div className="flex gap-4 overflow-x-auto pb-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-64 w-72 shrink-0" />
          ))}
        </div>
      </HiringModuleShell>
    );
  }

  if (pipe.isError) {
    const e = toApiError(pipe.error);
    const notAvailable =
      e.error === "not_found" || e.error === "forbidden";
    return (
      <HiringModuleShell title="Pipeline">
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-muted-foreground">
              {notAvailable
                ? "This pipeline isn't available. You need to be an HR admin or the requisition's hiring manager."
                : e.message}
            </p>
            <Button asChild variant="outline" className="gap-1.5">
              <Link to="/app/$tenantSlug/hiring/requisitions" params={{ tenantSlug }}>
                <ArrowLeft className="h-4 w-4" />
                Back to Requisitions
              </Link>
            </Button>
          </CardContent>
        </Card>
      </HiringModuleShell>
    );
  }

  const columns = pipe.data?.pipeline ?? [];
  const totalApps = columns.reduce(
    (n, c) => n + c.applications.length,
    0,
  );
  // K2.4 — "Not shortlisted" (screening knockout) filter. Knocked-out
  // applications are flagged, never auto-rejected; this only hides them
  // from the board view (they remain active + acted-on by a human).
  const knockoutCount = columns.reduce(
    (n, c) => n + c.applications.filter((a) => a.knockout_failed).length,
    0,
  );
  const searchable = (a: (typeof columns)[number]["applications"][number]) => {
    const [first, ...rest] = (a.candidate_name ?? "").split(" ");
    return {
      first_name: first,
      last_name: rest.join(" "),
      email: a.candidate_email,
      phone: a.candidate_phone,
    };
  };

  const visibleApps = (col: (typeof columns)[number]) =>
    col.applications.filter(
      (a) =>
        (!hideKnockout || !a.knockout_failed) &&
        matchesCandidateQuery(searchable(a), query),
    );
  const phoneStage =
    columns.find((c) => c.stage.id === phoneStageId)?.stage.id ??
    columns.find((c) => visibleApps(c).length > 0)?.stage.id ??
    columns[0]?.stage.id;

  return (
    <>
    <PhoneBackRow>
      <Link
        to="/app/$tenantSlug/hiring/requisitions"
        params={{ tenantSlug }}
        className={PHONE_BACK_LINK_CLASS}
      >
        <ArrowLeft className="h-4 w-4" />
        All Requisitions
      </Link>
    </PhoneBackRow>
    <HiringModuleShell
      title={reqTitle ? `Pipeline — ${reqTitle}` : "Pipeline"}
      description="Move candidates across stages. Rejecting needs a reason; the hire happens from a candidate's profile."
      actions={
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap [&>*]:px-3 sm:[&>*]:px-4">
          <Button asChild variant="outline" className="gap-1.5">
            <Link
              to="/app/$tenantSlug/hiring/requisitions/$id/report"
              params={{tenantSlug, id: String(id)}}
            >
              <BarChart3 className="h-4 w-4" />
              View Report
            </Link>
          </Button>
          <Button asChild variant="outline" className="gap-1.5">
            <Link
              to="/app/$tenantSlug/hiring/requisitions/$id"
              params={{tenantSlug, id: String(id)}}
            >
              <Pencil className="h-4 w-4" />
              <span className="sm:hidden">Edit Job</span>
              <span className="hidden sm:inline">Edit Requisition</span>
            </Link>
          </Button>
        </div>
      }
    >
      {totalApps === 0 && (
        <Alert className="mb-4">
          <AlertTitle>No candidates yet</AlertTitle>
          <AlertDescription>
            Candidates appear here when they apply via the careers page
            or when you import/attach them to this requisition from the
            Candidates page.
          </AlertDescription>
        </Alert>
      )}

      {totalApps > 0 && (
        <div className="relative mb-4 max-w-sm">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            aria-label="Search candidates on this board"
            placeholder="Search by name, email or phone"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-8"
          />
        </div>
      )}

      {knockoutCount > 0 && (
        <div className="mb-4 flex items-center gap-2">
          <Checkbox
            id="hide-knockout"
            checked={hideKnockout}
            onCheckedChange={(v) => setHideKnockout(v === true)}
          />
          <Label htmlFor="hide-knockout" className="cursor-pointer text-sm font-normal">
            Hide {knockoutCount} not-shortlisted (screening knockout)
          </Label>
        </div>
      )}

      {/* Phones: pick a stage, see its cards full width (the board was a
          2,000 px sideways scroll with an empty first column filling the
          screen). md+: the board. */}
      {columns.length > 0 && phoneStage != null && (
        <div className="mb-3 space-y-1.5 md:hidden">
          <Label htmlFor="pipeline-stage">Stage</Label>
          <Select
            value={String(phoneStage)}
            onValueChange={(v) => setPhoneStageId(Number(v))}
          >
            <SelectTrigger id="pipeline-stage" className="h-11 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {columns.map((col) => (
                <SelectItem key={col.stage.id} value={String(col.stage.id)}>
                  {col.stage.name} ({visibleApps(col).length})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex flex-col pb-4 md:flex-row md:gap-4 md:overflow-x-auto [@media(pointer:coarse)]:[&_:is(button,a.inline-flex)]:min-h-11">
        {columns.map((col) => {
          const apps = visibleApps(col);
          return (
            <div
              key={col.stage.id}
              className={cn(
                "w-full flex-col rounded-xl bg-muted/40 p-3 md:flex md:w-72 md:shrink-0",
                col.stage.id === phoneStage ? "flex" : "hidden",
              )}
            >
              <div className="mb-3 flex items-center justify-between px-1">
                <h3 className="text-sm font-semibold text-gray-900">
                  {col.stage.name}
                </h3>
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  {apps.length}
                </span>
              </div>
              <div className="flex-1 space-y-2">
                {apps.length === 0 ? (
                  <p className="px-1 py-6 text-center text-xs text-muted-foreground">
                    {query.trim()
                      ? "No matches in this stage."
                      : "No candidates in this stage."}
                  </p>
                ) : (
                  apps.map((app) => (
                    <ApplicationCard
                      key={app.id}
                      app={app}
                      stages={columns.map((c) => c.stage)}
                      candidateName={
                        app.candidate_name ||
                        app.candidate_email ||
                        `Candidate #${app.candidate_id}`
                      }
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </HiringModuleShell>
    </>
  );
}

function PipelineRoute() {
  const { id } = Route.useParams();
  const numId = Number(id);

  return (
    <HiringFeatureGate>
      {Number.isFinite(numId) && numId > 0 ? (
        <PipelineBoard id={numId} />
      ) : (
        <HiringModuleShell title="Pipeline">
          <Card>
            <CardContent className="py-16 text-center">
              <p className="text-sm text-muted-foreground">
                Invalid requisition.
              </p>
            </CardContent>
          </Card>
        </HiringModuleShell>
      )}
    </HiringFeatureGate>
  );
}
